import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  HairlineCard,
  Stack,
  TextField,
  Typography,
} from '@munifocus/ui';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import type { ChecklistItem } from '@munifocus/contracts-gov';
import { useChecklist, useUpdateChecklistItem } from '../lib/checklist-api';
import { localizeStatuteRef } from '../lib/file-display';
import { ChecklistVersions } from './ChecklistVersions';

/**
 * Step 5.1 digital legal checklist. Items the system can see (a letter was
 * generated, the notice was served, the seal was applied) pre-check themselves;
 * the officer can override any of them — check without the triggering event, or
 * uncheck with a note. Nothing here gates progression: it is the completeness
 * record that travels with the file to the Appeal Committee, council and Finance.
 */
function ChecklistRow({
  item,
  fileId,
  disabled,
}: {
  item: ChecklistItem;
  fileId: string;
  disabled: boolean;
}) {
  const { t } = useTranslation();
  const update = useUpdateChecklistItem(fileId);
  // The item's name is rendered as body text beside the box, not as a <label>,
  // so the checkbox had no accessible name at all (WCAG 4.1.2). Point at the
  // visible text rather than duplicating it into an aria-label, so the spoken
  // name and the visible label cannot drift apart (2.5.3).
  const labelId = useId();

  const set = (dto: Parameters<typeof update.mutate>[0]['dto']) =>
    update.mutate({ key: item.key, dto });

  return (
    <Stack spacing={0.5} sx={{ py: 1 }}>
      <Stack direction="row" sx={{ alignItems: 'flex-start', gap: 1 }}>
        <Checkbox
          size="small"
          sx={{ mt: -0.5 }}
          checked={item.checked}
          disabled={disabled || update.isPending}
          onChange={(e) => set({ checked: e.target.checked })}
          slotProps={{ input: { 'aria-labelledby': labelId } }}
        />
        <Stack sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" sx={{ alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="body2" id={labelId}>
              {t(`gov.checklist.items.${item.key}`)}
            </Typography>
            {item.statuteRef && (
              <Typography variant="caption" color="text.secondary">
                {localizeStatuteRef(item.statuteRef)}
              </Typography>
            )}
            {/* Make it obvious when the officer's state differs from the file. */}
            {item.overridden && (
              <Typography variant="caption" color="warning.main">
                {t('gov.checklist.overridden')}
              </Typography>
            )}
            {!item.overridden && item.auto && item.checked && (
              <Typography variant="caption" color="text.secondary">
                {t('gov.checklist.auto')}
              </Typography>
            )}
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 0.5 }}>
            {item.hasDate && (
              <TextField
                size="small"
                type="date"
                label={t('gov.checklist.date')}
                slotProps={{ inputLabel: { shrink: true } }}
                value={item.itemDate ?? ''}
                disabled={disabled || update.isPending}
                // Clearing sends null, not '': the server skips validation for
                // null but would reject '' as a non-date.
                onChange={(e) => set({ itemDate: e.target.value || null })}
              />
            )}
            <TextField
              size="small"
              fullWidth
              label={t('gov.checklist.note')}
              defaultValue={item.note ?? ''}
              disabled={disabled || update.isPending}
              // Commit on blur so we don't PATCH on every keystroke.
              onBlur={(e) => {
                if (e.target.value !== (item.note ?? '')) set({ note: e.target.value || null });
              }}
            />
            {item.overridden && (
              <Button
                size="small"
                disabled={disabled || update.isPending}
                onClick={() => set({ reset: true })}
              >
                {t('gov.checklist.reset')}
              </Button>
            )}
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
}

export function LegalChecklist({ fileId }: { fileId: string }) {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useChecklist(fileId);

  const items = data ?? [];
  const done = items.filter((i) => i.checked).length;
  const titleId = useId();

  return (
    // Checklist items read out fields the step's own form also carries, so the
    // set is a named group to keep the two contexts distinguishable to anyone
    // navigating by control rather than by sight. The registry item used to
    // duplicate the Step 5 checkbox's label exactly; it is now worded as the
    // read-out it is ("Registration recorded on the file"), so the names no
    // longer collide.
    <HairlineCard sx={{ p: 2 }} role="group" aria-labelledby={titleId}>
      <Stack direction="row" sx={{ alignItems: 'baseline', justifyContent: 'space-between' }}>
        <Typography variant="overline" id={titleId}>
          {t('gov.checklist.title')}
        </Typography>
        {items.length > 0 && (
          <Typography variant="caption" color="text.secondary">
            {t('gov.checklist.progress', { done, total: items.length })}
          </Typography>
        )}
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        {t('gov.checklist.hint')}
      </Typography>

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={22} />
        </Box>
      )}
      {isError && <Alert severity="error">{t('gov.checklist.loadError')}</Alert>}

      {!isLoading && !isError && (
        <Stack divider={<Divider flexItem />}>
          {items.map((item) => (
            <ChecklistRow key={item.key} item={item} fileId={fileId} disabled={false} />
          ))}
        </Stack>
      )}

      {!isLoading && !isError && <ChecklistVersions fileId={fileId} />}
    </HairlineCard>
  );
}
